import React, { useState, useEffect, useRef, useMemo, startTransition, useDeferredValue } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  Avatar,
  Chip,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Popover,
  Switch,
  FormControlLabel,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Backdrop,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete
} from '@mui/material';
import {
  Send as SendIcon,
  Group as GroupIcon,
  ExitToApp as LeaveIcon,
  PersonAdd as JoinIcon,
  EmojiEmotions as EmojiIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Image as ImageIcon,

  Close as CloseIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddReaction as AddReactionIcon
} from '@mui/icons-material';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase';
import { AdminPanel } from '../components/AdminPanel';
import { NotificationContainer, NotificationCardData } from '../components/NotificationCard';
import OnlineUsersIndicator from '../components/OnlineUsersIndicator';

import { translationService, SUPPORTED_LANGUAGES } from '../services/translationService';
import { useAuth } from '../contexts/AuthContext';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { useNavigate } from 'react-router-dom';
import { usePresence } from '../hooks/usePresence';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  getDoc,
  setDoc,
  limit,
  startAfter
} from 'firebase/firestore';
import { ref, getDownloadURL, getBlob } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../config/firebase';
import { setupCurrentUser } from '../utils/setupSampleData';
import notificationService from '../services/notificationService';
import ImageUploadService from '../services/imageUploadService';
import VoiceUploadService from '../services/voiceUploadService';
import FeatureGuard from '../components/FeatureGuard';
import MessageBubble from '../components/MessageBubble';
import VoiceRecorder, { VoiceMicButton } from '../components/VoiceRecorder';

interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  createdAt: any;
  lastActivity?: any;
  lastMessageAt?: any;
  adminOnlyPosting?: boolean;
}

export interface ChatMessage {
  id: string;
  userId: string;
  userDisplayName: string;
  content: string;
  timestamp: any;
  type?: 'text' | 'image' | 'voice';
  imageUrl?: string;
  imageName?: string;
  imageSize?: number;
  audioUrl?: string;
  audioName?: string;
  audioDuration?: number;
  editedAt?: any;
  isEdited?: boolean;
  reactions?: {
    [emoji: string]: {
      users: string[]; // Array of user IDs who reacted with this emoji
      count: number;
    };
  };
  replyTo?: {
    messageId: string;
    userId: string;
    userDisplayName: string;
    content: string;
    type?: 'text' | 'image';
  };
}

export interface GroupMember {
  userId: string;
  userDisplayName: string;
  joinedAt: any;
}

const CommunityContent: React.FC = () => {
  const { currentUser } = useAuth();
  const { settings } = useUserSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Add global styles for animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.05); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [groups, setGroups] = useState<Group[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const newMessage = useDeferredValue(inputValue);
  const [loading, setLoading] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingUserGroups, setLoadingUserGroups] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [lastMessageDoc, setLastMessageDoc] = useState<any>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [compressionStatus, setCompressionStatus] = useState<string | null>(null);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notificationPermissionStatus, setNotificationPermissionStatus] = useState<NotificationPermission>('default');
  const [fcmTokenRefreshInterval, setFcmTokenRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [inBrowserNotifications, setInBrowserNotifications] = useState<NotificationCardData[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Voice recording states
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [selectedAudio, setSelectedAudio] = useState<Blob | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const recordingTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const mentionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageForViewer, setSelectedImageForViewer] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');
  const [messageMenuAnchor, setMessageMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedMessageForMenu, setSelectedMessageForMenu] = useState<ChatMessage | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<ChatMessage | null>(null);
  const [updatingMessage, setUpdatingMessage] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [joinConfirmOpen, setJoinConfirmOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [groupToJoin, setGroupToJoin] = useState<Group | null>(null);
  const [groupToLeave, setGroupToLeave] = useState<Group | null>(null);
  const [reactionPickerAnchor, setReactionPickerAnchor] = useState<HTMLElement | null>(null);
  const [selectedMessageForReaction, setSelectedMessageForReaction] = useState<ChatMessage | null>(null);
  // Add states for WhatsApp-like quick reactions
  const [quickReactionAnchor, setQuickReactionAnchor] = useState<HTMLElement | null>(null);
  const [selectedMessageForQuickReaction, setSelectedMessageForQuickReaction] = useState<ChatMessage | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [isLongPress, setIsLongPress] = useState(false);
  
  // Add states for @mention functionality
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [mentionSuggestions, setMentionSuggestions] = useState<GroupMember[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const messageInputRef = React.useRef<HTMLInputElement>(null);
  
  // Add states for unread message tracking
  const [unreadCounts, setUnreadCounts] = useState<{[groupId: string]: number}>({});
  const [lastReadTimestamps, setLastReadTimestamps] = useState<{[groupId: string]: any}>({});

  // Initialize presence tracking
  usePresence({ 
    currentPage: selectedGroup ? 'group' : 'community',
    groupId: selectedGroup?.id
  });

  // Helper function to get translation status display
  const getTranslationStatusDisplay = () => {
    if (settings.translation.enabled) {
      const targetLang = settings.translation.targetLanguage === 'auto' 
        ? translationService.getUserLanguage() 
        : settings.translation.targetLanguage;
      
      const languageName = SUPPORTED_LANGUAGES[targetLang as keyof typeof SUPPORTED_LANGUAGES] || targetLang;
      
      return (
        <Chip
          size="small"
          variant="outlined"
          label={`🌍 ${languageName}`}
          sx={{
            fontSize: '0.7rem',
            height: '20px',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            color: 'rgba(255, 255, 255, 0.6)',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiChip-label': {
              px: 0.5
            }
          }}
          title={`Translation active: Messages will be translated to ${languageName}`}
        />
      );
    } else {
      // Show prompt to enable translation when it's disabled
      return (
        <Chip
          size="small"
          variant="outlined"
          label="🌍 Enable Translation?"
          clickable
          onClick={() => navigate('/settings')}
          sx={{
            fontSize: '0.7rem',
            height: '20px',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'rgba(255, 255, 255, 0.7)',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(25, 118, 210, 0.5)',
              backgroundColor: 'rgba(25, 118, 210, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              transform: 'scale(1.02)'
            },
            '& .MuiChip-label': {
              px: 0.5
            }
          }}
          title="Click to go to Settings and enable translation for messages in different languages"
        />
      );
    }
  };

  // Track page view on component mount
  useEffect(() => {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: 'Community',
        page_location: window.location.href,
        page_path: '/community'
      });
    }
  }, []);

  // Update overall loading state based on individual loading states
  useEffect(() => {
    setLoading(loadingGroups || loadingUserGroups);
  }, [loadingGroups, loadingUserGroups]);

  // Reset loading states when user changes
  useEffect(() => {
    if (!currentUser) {
      setLoadingGroups(true);
      setLoadingUserGroups(true);
    }
  }, [currentUser]);

  // Check if current user is admin and create user document if needed
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) return;
      
      try {
        let userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        // If user document doesn't exist, create it
        if (!userDoc.exists()) {
          console.log('User document not found, creating...');
          await setupCurrentUser(currentUser, false);
          userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        }
        
        if (userDoc.exists()) {
          const isUserAdmin = userDoc.data().isAdmin === true;
          setIsAdmin(isUserAdmin);
          
          // Initialize notifications and get user preferences
          const notifyEnabled = userDoc.data().notificationsEnabled !== false;
          setNotificationsEnabled(notifyEnabled);
          
          // Initialize notification service for this user with retry logic
          await initializeNotificationsWithRetry(currentUser.uid, notifyEnabled);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to load user information');
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  // Listen for in-browser notifications
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = notificationService.addNotificationListener((notifications) => {
      setInBrowserNotifications(notifications.map(n => ({
        id: n.id,
        title: n.title,
        body: n.body,
        groupId: n.groupId,
        groupName: n.groupName,
        senderName: n.senderName,
        timestamp: n.timestamp,
        avatar: n.avatar
      })));
    });

    // Load existing notifications
    const existingNotifications = notificationService.getInBrowserNotifications();
    setInBrowserNotifications(existingNotifications.map(n => ({
      id: n.id,
      title: n.title,
      body: n.body,
      groupId: n.groupId,
      groupName: n.groupName,
      senderName: n.senderName,
      timestamp: n.timestamp,
      avatar: n.avatar
    })));

    return unsubscribe;
  }, [currentUser]);

  // Update notification service preferences when settings change
  useEffect(() => {
    if (settings.notifications) {
      notificationService.setUserNotificationPreferences(settings.notifications);
    }
  }, [settings.notifications]);

  // Initialize notifications with retry logic and better error handling
  const initializeNotificationsWithRetry = async (userId: string, enabled: boolean, retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = 1000 * Math.pow(2, retryCount); // Exponential backoff

    try {
      // Check notification support
      if (!notificationService.isNotificationSupported()) {
        console.warn('Notifications not supported in this browser');
        return;
      }

      // Update permission status
      const permissionStatus = notificationService.getPermissionStatus();
      setNotificationPermissionStatus(permissionStatus);

      if (enabled && permissionStatus !== 'denied') {
        await notificationService.initializeForUser(userId);
        
        // Refresh FCM token periodically to ensure it stays valid
        await refreshFcmToken(userId);
        
        // Set up periodic token refresh (every 6 hours)
        if (fcmTokenRefreshInterval) {
          clearInterval(fcmTokenRefreshInterval);
        }
        
        const interval = setInterval(async () => {
          try {
            await refreshFcmToken(userId);
          } catch (error) {
            console.error('Periodic FCM token refresh failed:', error);
          }
        }, 6 * 60 * 60 * 1000); // 6 hours
        
        setFcmTokenRefreshInterval(interval);
        
        console.log('Notifications initialized successfully');
      }
    } catch (error) {
      console.error(`Notification initialization failed (attempt ${retryCount + 1}):`, error);
      
      if (retryCount < maxRetries) {
        console.log(`Retrying notification initialization in ${retryDelay}ms...`);
        setTimeout(() => {
          initializeNotificationsWithRetry(userId, enabled, retryCount + 1);
        }, retryDelay);
      } else {
        console.error('Failed to initialize notifications after all retries');
        setError('Failed to initialize push notifications. You may miss some messages.');
      }
    }
  };

  // Refresh FCM token with better error handling
  const refreshFcmToken = async (userId: string) => {
    try {
      const token = await notificationService.getNotificationToken(userId);
      if (token) {
        console.log('FCM token refreshed successfully');
        return token;
      } else {
        console.warn('No FCM token received during refresh');
      }
    } catch (error) {
      console.error('FCM token refresh failed:', error);
      // Don't show user error for token refresh failures unless it's a permission issue
      if (error instanceof Error && error.message?.includes('permission')) {
        setError('Please allow notifications in your browser settings to receive chat notifications.');
      }
      throw error;
    }
  };

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (fcmTokenRefreshInterval) {
        clearInterval(fcmTokenRefreshInterval);
      }
      // Clear notification context when component unmounts
      notificationService.clearGroupContext();
    };
  }, [fcmTokenRefreshInterval]);

  // Clear group context when component mounts (in case user navigated back)
  useEffect(() => {
    // Reset group context when entering community page
    if (!selectedGroup) {
      notificationService.clearGroupContext();
    }
  }, []); // Run only on mount

  // Monitor notification permission changes
  useEffect(() => {
    const checkPermissionStatus = () => {
      const currentStatus = notificationService.getPermissionStatus();
      if (currentStatus !== notificationPermissionStatus) {
        setNotificationPermissionStatus(currentStatus);
        
        if (currentStatus === 'granted' && notificationsEnabled && currentUser) {
          // Permission was granted, reinitialize notifications
          initializeNotificationsWithRetry(currentUser.uid, true);
        }
      }
    };

    // Check permission status periodically
    const permissionCheckInterval = setInterval(checkPermissionStatus, 5000);
    
    return () => clearInterval(permissionCheckInterval);
  }, [notificationPermissionStatus, notificationsEnabled, currentUser]);

  // Listen for messages from service worker for notification navigation
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NAVIGATE_TO_GROUP') {
        const targetGroupId = event.data.groupId;
        
        // Find the group and select it
        const targetGroup = groups.find(group => group.id === targetGroupId);
        if (targetGroup && userGroups.includes(targetGroupId)) {
          setSelectedGroup(targetGroup);
          console.log('Navigated to group from notification:', targetGroup.name);
        }
      }
    };

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Check URL parameters for group navigation (when app is opened from notification)
    const urlParams = new URLSearchParams(window.location.search);
    const groupParam = urlParams.get('group');
    if (groupParam && groups.length > 0) {
      const targetGroup = groups.find(group => group.id === groupParam);
      if (targetGroup && userGroups.includes(groupParam)) {
        setSelectedGroup(targetGroup);
        // Clean up URL parameter
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [groups, userGroups]);

  // Load available groups
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const groupsQuery = query(collection(db, 'groups'), orderBy('createdAt', 'desc'));
        const groupsSnapshot = await getDocs(groupsQuery);
        
        const groupsData = await Promise.all(
          groupsSnapshot.docs.map(async (groupDoc) => {
            try {
              // Count members for each group
              const membersQuery = collection(db, 'groups', groupDoc.id, 'members');
              const membersSnapshot = await getDocs(membersQuery);
              
              return {
                id: groupDoc.id,
                ...groupDoc.data(),
                memberCount: membersSnapshot.size
              };
            } catch (memberError) {
              console.error('Error counting members for group', groupDoc.id, ':', memberError);
              return {
                id: groupDoc.id,
                ...groupDoc.data(),
                memberCount: 0
              };
            }
          })
        );
        
        console.log('Community: Loaded groups with member counts:', groupsData);
        setGroups(groupsData as Group[]);
      } catch (error) {
        console.error('Error loading groups:', error);
        setError('Failed to load groups');
      } finally {
        setLoadingGroups(false);
      }
    };

    loadGroups();
  }, []);

  // Function to refresh group member counts
  const refreshGroupCounts = async () => {
    try {
      const groupsQuery = query(collection(db, 'groups'), orderBy('createdAt', 'desc'));
      const groupsSnapshot = await getDocs(groupsQuery);
      
      const groupsData = await Promise.all(
        groupsSnapshot.docs.map(async (groupDoc) => {
          try {
            const membersQuery = collection(db, 'groups', groupDoc.id, 'members');
            const membersSnapshot = await getDocs(membersQuery);
            
            return {
              id: groupDoc.id,
              ...groupDoc.data(),
              memberCount: membersSnapshot.size
            };
          } catch (memberError) {
            console.error('Error counting members for group', groupDoc.id, ':', memberError);
            return {
              id: groupDoc.id,
              ...groupDoc.data(),
              memberCount: 0
            };
          }
        })
      );
      
      setGroups(groupsData as Group[]);
    } catch (error) {
      console.error('Error refreshing group counts:', error);
    }
  };

  // Load group members for @mention functionality
  const loadGroupMembers = async (groupId: string) => {
    if (!groupId) return;
    
    try {
      const membersQuery = collection(db, 'groups', groupId, 'members');
      const membersSnapshot = await getDocs(membersQuery);
      
      const members: GroupMember[] = membersSnapshot.docs.map(doc => ({
        userId: doc.id,
        userDisplayName: doc.data().userDisplayName || 'Anonymous',
        joinedAt: doc.data().joinedAt
      }));
      
      setGroupMembers(members);
    } catch (error) {
      console.error('Error loading group members:', error);
    }
  };

  // Load last read timestamps for unread message tracking
  const loadLastReadTimestamps = async () => {
    if (!currentUser) return;
    
    try {
      const userReadDataRef = doc(db, 'users', currentUser.uid, 'readData', 'groups');
      const userReadDataDoc = await getDoc(userReadDataRef);
      
      if (userReadDataDoc.exists()) {
        const readData = userReadDataDoc.data();
        setLastReadTimestamps(readData || {});
      }
    } catch (error: any) {
      // Silently handle permissions error - unread counts will be disabled
      if (error?.code === 'permission-denied') {
        console.log('Unread message tracking disabled: insufficient permissions');
      } else {
        console.error('Error loading last read timestamps:', error);
      }
    }
  };

  // Update last read timestamp for a group
  const updateLastReadTimestamp = async (groupId: string) => {
    if (!currentUser) return;
    
    try {
      const now = new Date();
      const userReadDataRef = doc(db, 'users', currentUser.uid, 'readData', 'groups');
      
      await setDoc(userReadDataRef, {
        [groupId]: now
      }, { merge: true });
      
      // Update local state
      setLastReadTimestamps(prev => ({
        ...prev,
        [groupId]: now
      }));
      
      // Reset unread count for this group
      setUnreadCounts(prev => ({
        ...prev,
        [groupId]: 0
      }));
    } catch (error: any) {
      // Silently handle permissions error - unread counts will be disabled
      if (error?.code !== 'permission-denied') {
        console.error('Error updating last read timestamp:', error);
      }
    }
  };

  // Calculate unread count for a group
  const calculateUnreadCount = (groupId: string, messages: ChatMessage[]): number => {
    if (!currentUser) return 0;
    
    const lastRead = lastReadTimestamps[groupId];
    if (!lastRead) return messages.length; // If never read, all messages are unread
    
    const lastReadTime = lastRead instanceof Date ? lastRead : lastRead.toDate?.() || new Date(lastRead);
    
    return messages.filter(message => {
      if (message.userId === currentUser.uid) return false; // Don't count own messages
      if (!message.timestamp) return false;
      
      const messageTime = message.timestamp.toDate();
      return messageTime > lastReadTime;
    }).length;
  };

  // Load user's group memberships and read timestamps
  useEffect(() => {
    if (!currentUser) {
      setLoadingUserGroups(false);
      return;
    }

    const loadUserGroups = async () => {
      try {
        setLoadingUserGroups(true);
        // Get all groups first
        const groupsSnapshot = await getDocs(collection(db, 'groups'));
        const userGroupIds: string[] = [];
        
        // Check each group for membership
        for (const groupDoc of groupsSnapshot.docs) {
          const memberDoc = await getDoc(doc(db, 'groups', groupDoc.id, 'members', currentUser.uid));
          if (memberDoc.exists()) {
            userGroupIds.push(groupDoc.id);
          }
        }
        
        setUserGroups(userGroupIds);
        
        // Load read timestamps after loading groups
        await loadLastReadTimestamps();
      } catch (error) {
        console.error('Error loading user groups:', error);
        setError('Failed to load user group memberships');
      } finally {
        setLoadingUserGroups(false);
      }
    };

    loadUserGroups();
  }, [currentUser]);

  // Track unread counts for user's groups
  useEffect(() => {
    if (!currentUser || userGroups.length === 0 || Object.keys(lastReadTimestamps).length === 0) return;

    const unsubscribes: (() => void)[] = [];

    // Set up listeners for each user group to track unread messages
    userGroups.forEach(groupId => {
      const messagesQuery = query(
        collection(db, 'groups', groupId, 'messages'),
        orderBy('timestamp', 'desc'),
        limit(50) // Only check recent messages for performance
      );

      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        // Use startTransition for unread count calculations to prevent blocking
        startTransition(() => {
          const messages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as ChatMessage[];

          const unreadCount = calculateUnreadCount(groupId, messages);
          
          setUnreadCounts(prev => ({
            ...prev,
            [groupId]: unreadCount
          }));
        });
      }, (error) => {
        console.error(`Error listening to messages for group ${groupId}:`, error);
      });

      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [currentUser, userGroups, lastReadTimestamps]);

  // Load initial messages for selected group (recent messages only)
  useEffect(() => {
    if (!selectedGroup || !currentUser) return;

    // Reset pagination state when switching groups
    setMessages([]);
    setHasMoreMessages(true);
    setLastMessageDoc(null);
    setInitialLoad(true);

    // Load group members for @mention functionality
    loadGroupMembers(selectedGroup.id);

    // Mark messages as read when opening chat
    updateLastReadTimestamp(selectedGroup.id);

    // Update notification service with current group context
    notificationService.setCurrentGroup(selectedGroup.id);

    // Load only the most recent 50 messages initially
    const messagesQuery = query(
      collection(db, 'groups', selectedGroup.id, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      // Use startTransition to make message processing non-blocking
      startTransition(() => {
        const messagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ChatMessage[];
        
        // Reverse to show oldest first (since we queried desc)
        const sortedMessages = messagesData.reverse();
        setMessages(sortedMessages);
        
        // Set pagination state
        if (snapshot.docs.length > 0) {
          setLastMessageDoc(snapshot.docs[snapshot.docs.length - 1]); // Last doc in desc order = oldest message
          setHasMoreMessages(snapshot.docs.length === 50); // If we got 50, there might be more
        } else {
          setHasMoreMessages(false);
        }
        
        setInitialLoad(false);
      });
    });

    return () => {
      unsubscribe();
      // Clear current group context when leaving chat
      notificationService.setCurrentGroup(null);
    };
  }, [selectedGroup, currentUser]);

  // Load older messages function
  const loadOlderMessages = async () => {
    if (!selectedGroup || !lastMessageDoc || !hasMoreMessages || loadingOlderMessages) return;

    setLoadingOlderMessages(true);
    try {
      const olderMessagesQuery = query(
        collection(db, 'groups', selectedGroup.id, 'messages'),
        orderBy('timestamp', 'desc'),
        startAfter(lastMessageDoc),
        limit(30)
      );

      const snapshot = await getDocs(olderMessagesQuery);
      
      if (snapshot.docs.length > 0) {
        // Use startTransition for processing older messages to prevent blocking
        startTransition(() => {
          const olderMessages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as ChatMessage[];
          
          // Reverse to show oldest first and prepend to existing messages
          const sortedOlderMessages = olderMessages.reverse();
          setMessages(prev => [...sortedOlderMessages, ...prev]);
          
          // Update pagination state
          setLastMessageDoc(snapshot.docs[snapshot.docs.length - 1]);
          setHasMoreMessages(snapshot.docs.length === 30);
        });
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
      setError('Failed to load older messages');
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  // Auto-scroll to bottom for new messages (when user is already at bottom)
  useEffect(() => {
    if (messagesContainerRef.current && !loadingOlderMessages && !initialLoad) {
      const container = messagesContainerRef.current;
      // Only auto-scroll if user is already at the bottom (for new messages)
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages, loadingOlderMessages, initialLoad]);

  // Cleanup image preview on unmount
  useEffect(() => {
    return () => {
      if (imagePreview) {
        ImageUploadService.revokePreviewUrl(imagePreview);
      }
    };
  }, [imagePreview]);

  // Cleanup audio preview and recording on unmount
  useEffect(() => {
    return () => {
      if (audioPreview) {
        URL.revokeObjectURL(audioPreview);
      }
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      stopRecordingTimer();
    };
  }, [audioPreview, audioStream]);

  const handleJoinGroupClick = (group: Group) => {
    setGroupToJoin(group);
    setJoinConfirmOpen(true);
  };

  const handleConfirmJoinGroup = async () => {
    if (!currentUser || !groupToJoin) return;

    // Track join group attempt
    if (analytics) {
      logEvent(analytics, 'group_join_attempt', {
        group_id: groupToJoin.id,
        group_name: groupToJoin.name,
        member_count: groupToJoin.memberCount,
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }

    try {
      // Check if user is banned from this group
      const bannedDoc = await getDoc(doc(db, 'groups', groupToJoin.id, 'banned', currentUser.uid));
      if (bannedDoc.exists()) {
        setError('You have been banned from this group and cannot join.');
        setJoinConfirmOpen(false);
        setGroupToJoin(null);
        
        // Track banned user attempt
        if (analytics) {
          logEvent(analytics, 'group_join_blocked', {
            group_id: groupToJoin.id,
            group_name: groupToJoin.name,
            reason: 'banned',
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        return;
      }

      const memberDoc = doc(db, 'groups', groupToJoin.id, 'members', currentUser.uid);
      await setDoc(memberDoc, {
        userId: currentUser.uid,
        userDisplayName: currentUser.displayName || 'Anonymous',
        joinedAt: serverTimestamp()
      });
      
      setUserGroups(prev => [...prev, groupToJoin.id]);
      await refreshGroupCounts();
      
      setJoinConfirmOpen(false);
      setGroupToJoin(null);

      // Track successful group join
      if (analytics) {
        logEvent(analytics, 'group_join_success', {
          group_id: groupToJoin.id,
          group_name: groupToJoin.name,
          member_count: groupToJoin.memberCount + 1,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } catch (error) {
      console.error('Error joining group:', error);
      setError('Failed to join group');
      setJoinConfirmOpen(false);
      setGroupToJoin(null);

      // Track group join error
      if (analytics) {
        logEvent(analytics, 'group_join_error', {
          group_id: groupToJoin.id,
          group_name: groupToJoin.name,
          error_message: error instanceof Error ? error.message : 'Unknown error',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    }
  };

  const handleCancelJoinGroup = () => {
    setJoinConfirmOpen(false);
    setGroupToJoin(null);
  };

  const handleLeaveGroupClick = (group: Group) => {
    setGroupToLeave(group);
    setLeaveConfirmOpen(true);
  };

  const handleConfirmLeaveGroup = async () => {
    if (!currentUser || !groupToLeave) return;

    // Track leave group attempt
    if (analytics) {
      logEvent(analytics, 'group_leave_attempt', {
        group_id: groupToLeave.id,
        group_name: groupToLeave.name,
        member_count: groupToLeave.memberCount,
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }

    try {
      const memberDoc = doc(db, 'groups', groupToLeave.id, 'members', currentUser.uid);
      await deleteDoc(memberDoc);
      
      setUserGroups(prev => prev.filter(id => id !== groupToLeave.id));
      
      if (selectedGroup?.id === groupToLeave.id) {
        setSelectedGroup(null);
        notificationService.setCurrentGroup(null);
      }
      await refreshGroupCounts();
      
      setLeaveConfirmOpen(false);
      setGroupToLeave(null);

      // Track successful group leave
      if (analytics) {
        logEvent(analytics, 'group_leave_success', {
          group_id: groupToLeave.id,
          group_name: groupToLeave.name,
          member_count: groupToLeave.memberCount - 1,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      setError('Failed to leave group');
      setLeaveConfirmOpen(false);
      setGroupToLeave(null);

      // Track group leave error
      if (analytics) {
        logEvent(analytics, 'group_leave_error', {
          group_id: groupToLeave.id,
          group_name: groupToLeave.name,
          error_message: error instanceof Error ? error.message : 'Unknown error',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    }
  };

  const handleCancelLeaveGroup = () => {
    setLeaveConfirmOpen(false);
    setGroupToLeave(null);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser || !selectedGroup) return;
    
    // Check if we have text message, image, or voice message to send
    if (!inputValue.trim() && !selectedImage && !selectedAudio) return;

    setSendingMessage(true);
    try {
      // Handle editing existing message
      if (editingMessageId) {
        const messageRef = doc(db, 'groups', selectedGroup.id, 'messages', editingMessageId);
        await updateDoc(messageRef, {
          content: newMessage.trim(),
          editedAt: serverTimestamp(),
          isEdited: true
        });
        
        console.log('✏️ Message edited successfully:', editingMessageId);
        
        // Track successful message edit (non-blocking)
        if (analytics) {
          setTimeout(() => {
            logEvent(analytics, 'message_edit_success', {
              group_id: selectedGroup.id,
              group_name: selectedGroup.name,
              message_id: editingMessageId,
              content_length: newMessage.trim().length,
              device_type: isMobile ? 'mobile' : 'desktop'
            });
          }, 0);
        }
        
        // Clear edit state
        setEditingMessageId(null);
        setEditingContent('');
        setInputValue('');
        
        return; // Don't continue with new message creation
      }

      let messageData: any = {
        userId: currentUser.uid,
        userDisplayName: currentUser.displayName || 'Anonymous',
        timestamp: serverTimestamp()
      };

      // Add reply information if replying to a message
      if (replyingTo) {
        messageData.replyTo = {
          messageId: replyingTo.id,
          userId: replyingTo.userId,
          userDisplayName: replyingTo.userDisplayName,
          content: replyingTo.content,
          type: replyingTo.type || 'text'
        };
      }

            // Handle image message
      if (selectedImage) {
        setUploadingImage(true);
        try {
          const uploadResult = await ImageUploadService.uploadChatImage(
            selectedImage,
            currentUser.uid,
            selectedGroup.id
          );

          messageData = {
            ...messageData,
            content: newMessage.trim() || `Shared an image: ${selectedImage.name}`,
            type: 'image',
            imageUrl: uploadResult.url,
            imageName: uploadResult.fileName,
            imageSize: uploadResult.size
          };
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload image');
          return;
        } finally {
          setUploadingImage(false);
        }
      }

      // Handle voice message
      if (selectedAudio) {
        setUploadingAudio(true);
        try {
          const uploadResult = await VoiceUploadService.uploadChatAudio(
            selectedAudio,
            currentUser.uid,
            selectedGroup.id,
            recordingDuration
          );

          messageData = {
            ...messageData,
            content: newMessage.trim() || `🎤 Voice message (${VoiceUploadService.formatDuration(recordingDuration)})`,
            type: 'voice',
            audioUrl: uploadResult.url,
            audioName: uploadResult.fileName,
            audioDuration: recordingDuration
          };
        } catch (uploadError) {
          console.error('Error uploading audio:', uploadError);
          setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload voice message');
          return;
        } finally {
          setUploadingAudio(false);
        }
      }

      if (!selectedImage && !selectedAudio) {
        // Handle text message
        messageData = {
          ...messageData,
          content: newMessage.trim(),
          type: 'text'
        };
      }

      console.log('📨 Sending message to group:', selectedGroup.id, 'Type:', messageData.type);
      
      const messageDoc = await addDoc(collection(db, 'groups', selectedGroup.id, 'messages'), messageData);
      
      console.log('📨 Message sent successfully:', messageDoc.id);
      console.log('📨 This should trigger Cloud Function for notifications...');
      
      // Track successful message send (non-blocking)
      if (analytics) {
        setTimeout(() => {
          logEvent(analytics, 'message_send_success', {
            group_id: selectedGroup.id,
            group_name: selectedGroup.name,
            message_type: messageData.type,
            has_reply: !!replyingTo,
            content_length: messageData.content.length,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }, 0);
      }
      
      // Clear form
      setInputValue('');
      clearSelectedImage();
      clearSelectedAudio();
      setReplyingTo(null);
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');

      // Track message send error (non-blocking)
      if (analytics) {
        setTimeout(() => {
          logEvent(analytics, 'message_send_error', {
            group_id: selectedGroup?.id,
            group_name: selectedGroup?.name,
            message_type: selectedImage ? 'image' : 'text',
            error_message: error instanceof Error ? error.message : 'Unknown error',
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }, 0);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Track image selection start (non-blocking)
    if (analytics) {
      setTimeout(() => {
        logEvent(analytics, 'community_image_select_start', {
          file_size: file.size,
          file_type: file.type,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }, 0);
    }

    try {
      // Clear previous states
      setError(null);
      setCompressionStatus(null);

      // Show loading state if compression is needed
      if (file.size > 1024 * 1024) {
        setCompressionStatus(`🗜️ Compressing image (${(file.size / 1024 / 1024).toFixed(1)}MB)...`);
      }

      // Create preview with automatic compression for large files
      const previewResult = await ImageUploadService.createPreviewUrlWithCompression(file);
      
      // Use compressed file if available
      const finalFile = previewResult.compressedFile || file;
      
      setSelectedImage(finalFile);
      setImagePreview(previewResult.url);
      
      // Show compression success message
      if (previewResult.compressedFile) {
        const reductionPercent = ((1 - finalFile.size / file.size) * 100).toFixed(1);
        setCompressionStatus(`✅ Image compressed: ${(finalFile.size / 1024 / 1024).toFixed(1)}MB (${reductionPercent}% smaller)`);
        
        // Clear the success message after 4 seconds
        setTimeout(() => {
          setCompressionStatus(null);
        }, 4000);

        // Track image compression success (non-blocking)
        if (analytics) {
          setTimeout(() => {
            logEvent(analytics, 'community_image_compressed', {
              original_size: file.size,
              compressed_size: finalFile.size,
              reduction_percent: parseFloat(reductionPercent),
              device_type: isMobile ? 'mobile' : 'desktop'
            });
          }, 0);
        }
      } else {
        setCompressionStatus(null);
      }

      // Track successful image selection (non-blocking)
      if (analytics) {
        setTimeout(() => {
          logEvent(analytics, 'community_image_select_success', {
            file_size: finalFile.size,
            file_type: finalFile.type,
            was_compressed: !!previewResult.compressedFile,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }, 0);
      }
    } catch (error) {
      console.error('Error selecting image:', error);
      setError(error instanceof Error ? error.message : 'Invalid image file');
      setCompressionStatus(null);

      // Track image selection error (non-blocking)
      if (analytics) {
        setTimeout(() => {
          logEvent(analytics, 'community_image_select_error', {
            file_size: file.size,
            file_type: file.type,
            error_message: error instanceof Error ? error.message : 'Unknown error',
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }, 0);
      }
    }
  };

  const clearSelectedImage = () => {
    if (imagePreview) {
      ImageUploadService.revokePreviewUrl(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
    setCompressionStatus(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Voice recording functions
  const startRecordingTimer = () => {
    setRecordingDuration(0);
    recordingTimerRef.current = setInterval(() => {
      setRecordingDuration(prev => {
        const newDuration = prev + 1;
        // Auto-stop recording at 5 minutes
        if (newDuration >= 300) {
          handleStopRecording();
        }
        return newDuration;
      });
    }, 1000);
  };

  const stopRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  // Audio compression configuration
  const getSupportedMimeTypeWithOptions = (): { mimeType: string, options: any } => {
    // Prioritize compressed formats with low bitrate for voice
    const compressionConfigs = [
      { 
        type: 'audio/webm;codecs=opus', 
        options: { audioBitsPerSecond: 24000 } // 24kbps - good for voice
      },
      { 
        type: 'audio/ogg;codecs=opus', 
        options: { audioBitsPerSecond: 24000 }
      },
      { 
        type: 'audio/webm', 
        options: { audioBitsPerSecond: 32000 } // 32kbps fallback
      },
      { 
        type: 'audio/mp4', 
        options: { audioBitsPerSecond: 32000 }
      },
      { 
        type: 'audio/ogg', 
        options: { audioBitsPerSecond: 48000 } // Higher for compatibility
      }
    ];

    // Find the first supported format with compression
    for (const config of compressionConfigs) {
      if (MediaRecorder.isTypeSupported(config.type)) {
        return { 
          mimeType: config.type, 
          options: config.options 
        };
      }
    }

    // Ultimate fallback without specific codec
    return { 
      mimeType: 'audio/webm', 
      options: { audioBitsPerSecond: 32000 }
    };
  };

  // Keep legacy function for compatibility
  const getSupportedMimeType = (): string => {
    const { mimeType } = getSupportedMimeTypeWithOptions();
    return mimeType.split(';')[0]; // Remove codec specification for simple usage
  };

  const handleMicButtonClick = async () => {
    if (recording) {
      handleStopRecording();
      return;
    }

    if (!VoiceUploadService.isRecordingSupported()) {
      setError('Voice recording is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 22050  // Reduced from 44100 to 22050 for voice
        } 
      });

      const { mimeType, options } = getSupportedMimeTypeWithOptions();
      const recorder = new MediaRecorder(stream, { 
        mimeType, 
        ...options 
      });
      
      const chunks: Blob[] = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: mimeType });
        setSelectedAudio(audioBlob);
        setAudioPreview(URL.createObjectURL(audioBlob));
        
        // Stop and cleanup stream
        stream.getTracks().forEach(track => track.stop());
        setAudioStream(null);
      };

      recorder.onerror = (event) => {
        console.error('Recording error:', event);
        setError('Recording failed. Please try again.');
        setRecording(false);
        stopRecordingTimer();
        stream.getTracks().forEach(track => track.stop());
        setAudioStream(null);
      };

      setMediaRecorder(recorder);
      setAudioStream(stream);
      recorder.start();
      setRecording(true);
      startRecordingTimer();

    } catch (error) {
      console.error('Error accessing microphone:', error);
      if (error instanceof Error && error.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access to record voice messages.');
      } else {
        setError('Failed to access microphone. Please check your device settings.');
      }
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder && recording) {
      mediaRecorder.stop();
      setRecording(false);
      stopRecordingTimer();
      setMediaRecorder(null);
    }
  };

  const clearSelectedAudio = () => {
    if (audioPreview) {
      URL.revokeObjectURL(audioPreview);
    }
    setSelectedAudio(null);
    setAudioPreview(null);
    setRecordingDuration(0);
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
    '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
    '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪',
    '😎', '🤓', '🧐', '🤔', '🤭', '🤫', '🤐', '🤨',
    '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
    '😔', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩',
    '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙',
    '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️',
    '🖖', '👋', '🤝', '👏', '🙌', '👐', '🤲', '🤜',
    '🤛', '✊', '👊', '🤳', '💪', '🦾', '🖕', '✍️',
    '🙏', '🦶', '🦵', '💄', '💋', '👄', '🦷', '👅',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
    '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖',
    '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️',
    '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈',
    '🔥', '💯', '💢', '💥', '💫', '💦', '💨', '🕳️',
    '💣', '💬', '👁️‍🗨️', '🗨️', '🗯️', '💭', '💤', '👋'
  ];

  const handleEmojiClick = (emoji: string) => {
    setInputValue((prev: string) => prev + emoji);
    setEmojiAnchorEl(null);
  };

  const handleEmojiOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  // Handle @mention functionality - ultra-optimized for performance
  const handleMessageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // ONLY update input value synchronously - nothing else!
    setInputValue(value);
    
    // Defer ALL other processing completely to next event loop tick
    setTimeout(() => {
      startTransition(() => {
        // Only do mention processing if there's an @ symbol and text is reasonably short
        if (value.includes('@') && value.length < 300) {
          const cursorPos = e.target.selectionStart || 0;
          setCursorPosition(cursorPos);
          
          // Check if user is typing @ mention
          const beforeCursor = value.substring(0, cursorPos);
          const mentionMatch = beforeCursor.match(/@(\w*)$/);
          
          if (mentionMatch) {
            const query = mentionMatch[1].toLowerCase();
            setMentionQuery(query);
            
            // Debounce the mention filtering to prevent lag during typing
            if (mentionTimeoutRef.current) {
              clearTimeout(mentionTimeoutRef.current);
            }
            mentionTimeoutRef.current = setTimeout(() => {
              // Filter members based on query
              const filteredMembers = groupMembers.filter(member =>
                member.userDisplayName.toLowerCase().includes(query) &&
                member.userId !== currentUser?.uid // Don't show current user
              );
              
              setMentionSuggestions(filteredMembers.slice(0, 5)); // Limit to 5 suggestions
              setShowMentions(true);
            }, 200); // Increased debounce to 200ms for better performance
            return; // Exit early to avoid clearing mentions
          }
        }
        
        // Clear mentions if no @ symbol or not in mention context
        if (showMentions) {
          setShowMentions(false);
          setMentionSuggestions([]);
          setMentionQuery('');
          if (mentionTimeoutRef.current) {
            clearTimeout(mentionTimeoutRef.current);
          }
        }
      });
    }, 0);
  };

  const handleMentionSelect = (member: GroupMember) => {
    const beforeCursor = inputValue.substring(0, cursorPosition);
    const afterCursor = inputValue.substring(cursorPosition);
    
    // Find the @ position
    const mentionStart = beforeCursor.lastIndexOf('@');
    const beforeMention = beforeCursor.substring(0, mentionStart);
    
    // Replace with @username format
    const newText = `${beforeMention}@${member.userDisplayName} ${afterCursor}`;
    setInputValue(newText);
    
    // Close mentions
    setShowMentions(false);
    setMentionSuggestions([]);
    setMentionQuery('');
    
    // Focus back to input
    setTimeout(() => {
      if (messageInputRef.current) {
        const newCursorPos = beforeMention.length + member.userDisplayName.length + 2; // +2 for @ and space
        messageInputRef.current.focus();
        messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showMentions && mentionSuggestions.length > 0) {
      if (e.key === 'Escape') {
        setShowMentions(false);
        setMentionSuggestions([]);
      }
    }
  };

  const renderMessageContent = (content: string) => {
    // Combined regex to capture @mentions or URLs
    //  - Group 1: @username (letters, numbers, underscores)
    //  - Group 2: http/https URLs (stops at whitespace)
    const pattern = /(@[\w-]+)|(https?:\/\/[^\s]+)/g;

    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    // Iterate through all matches (mentions or links)
    while ((match = pattern.exec(content)) !== null) {
      // Push plain text that appears before the current match
      if (match.index > lastIndex) {
        elements.push(content.slice(lastIndex, match.index));
      }

      if (match[1]) {
        // This is an @mention
        const mentionText = match[1]; // Includes the leading '@'
        elements.push(
          <span
            key={elements.length}
            style={{
              backgroundColor: 'rgba(33, 150, 243, 0.2)',
              color: '#2196f3',
              padding: '2px 4px',
              borderRadius: '4px',
              fontWeight: 500,
            }}
          >
            {mentionText}
          </span>
        );
      } else if (match[2]) {
        // This is a URL – make it clickable
        const url = match[2];
        elements.push(
          <a
            key={elements.length}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#90caf9', textDecoration: 'underline' }}
          >
            {url}
          </a>
        );
      }

      lastIndex = pattern.lastIndex;
    }

    // Push any remaining text after the last match
    if (lastIndex < content.length) {
      elements.push(content.slice(lastIndex));
    }

    return elements;
  };

  const handleNotificationsToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentUser) return;

    const newValue = event.target.checked;
    
    // Track notification preference change (non-blocking)
    if (analytics) {
      setTimeout(() => {
        logEvent(analytics, 'notification_toggle', {
          enabled: newValue,
          previous_state: notificationsEnabled,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }, 0);
    }
    
    // Update UI immediately for responsive feel
    setNotificationsEnabled(newValue);

    try {
      await notificationService.updateNotificationPreferences(currentUser.uid, newValue);
      
      if (newValue) {
        // Re-initialize notifications when enabled
        await initializeNotificationsWithRetry(currentUser.uid, true);
      } else {
        // Clear token refresh interval when disabled
        if (fcmTokenRefreshInterval) {
          clearInterval(fcmTokenRefreshInterval);
          setFcmTokenRefreshInterval(null);
        }
      }

      // Track successful notification preference update
      if (analytics) {
        logEvent(analytics, 'notification_toggle_success', {
          enabled: newValue,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      setError('Failed to update notification preferences');
      // Revert the toggle if the operation failed
      setNotificationsEnabled(!newValue);

      // Track notification preference update error
      if (analytics) {
        logEvent(analytics, 'notification_toggle_error', {
          enabled: newValue,
          error_message: error instanceof Error ? error.message : 'Unknown error',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    }
  };

  const handleTestNotification = async () => {
    if (!currentUser || !isAdmin) return;

    try {
      // Call the Cloud Function to send a test notification
      const sendTestNotification = httpsCallable(functions, 'sendTestNotification');

      // Call the Cloud Function to send a test notification
      const result = await sendTestNotification({
        userId: currentUser.uid,
        title: 'Test Notification from T1 FX Team',
        body: 'This is a test notification to verify your push notifications are working correctly!'
      });

      console.log('Test notification result:', result.data);
      
      const data = result.data as { success: boolean; sent: number; failed: number; total: number };
      
      if (data.success && data.sent > 0) {
        setError(null);
        alert(`Test notification sent successfully! ${data.sent} out of ${data.total} tokens worked.`);
      } else if (data.success && data.sent === 0) {
        setError(`All FCM tokens failed (${data.failed}/${data.total}). Refreshing tokens...`);
        
        // All tokens failed - let's refresh them
        await handleRefreshTokens();
      } else {
        setError('Failed to send test notification via Cloud Function');
      }
      
    } catch (error) {
      console.error('Error sending test notification via Cloud Function:', error);
      
      // Fallback: show a browser notification directly
      try {
        if (notificationPermissionStatus === 'granted') {
          new Notification('Test Notification from T1 FX Team', {
            body: 'This is a test notification to verify your setup is working! (Fallback mode)',
            icon: '/images/Logos/T1 Logo Black200.png',
            tag: 'test-notification',
            requireInteraction: true
          });
          
          setError(null);
          alert('Fallback test notification shown! The Cloud Function may not be deployed yet, but local notifications work.');
        } else {
          setError('Notification permission not granted. Please enable notifications and try again.');
        }
      } catch (fallbackError) {
        console.error('Fallback notification failed:', fallbackError);
        setError('Failed to send test notification. Please check your notification settings and ensure the Cloud Function is deployed.');
      }
    }
  };

  const handleRefreshTokens = async () => {
    if (!currentUser) return;

    try {
      console.log('Refreshing FCM tokens...');
      
      // First, clear all existing tokens
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, {
        fcmTokens: [],
        lastTokenCleanup: new Date(),
        tokenRefreshReason: 'Manual refresh due to failed notifications'
      }, { merge: true });
      
      console.log('Cleared old tokens, getting new token...');
      
      // Get a fresh token
      const newToken = await notificationService.getNotificationToken(currentUser.uid);
      
      if (newToken) {
        console.log('New FCM token obtained successfully');
        alert('FCM tokens refreshed successfully! Try the test notification again.');
        setError(null);
      } else {
        setError('Failed to obtain new FCM token. Please check your notification permissions.');
      }
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      setError('Failed to refresh FCM tokens. Please try reloading the page.');
    }
  };

  const handleDebugTokens = async () => {
    if (!currentUser || !isAdmin) return;

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const fcmTokens = userData.fcmTokens || [];
        const notificationsEnabled = userData.notificationsEnabled;
        const lastTokenUpdate = userData.lastTokenUpdate;
        
        alert(`Token Debug Info:
- User ID: ${currentUser.uid}
- Token Count: ${fcmTokens.length}
- Notifications Enabled: ${notificationsEnabled}
- Permission Status: ${notificationPermissionStatus}
- Last Update: ${lastTokenUpdate?.toDate?.() || 'Unknown'}`);
      } else {
        alert('User document not found');
      }
    } catch (error) {
      console.error('Error debugging tokens:', error);
      setError('Failed to debug tokens');
    }
  };

     const handleDebugNotificationContext = async () => {
     if (!currentUser || !isAdmin) return;

     try {
       const notificationContext = await notificationService.getCurrentNotificationContext() as any;
       
       alert(`Notification Debug Info:
📍 Current Page: ${window.location.pathname}  
👁️ Page Visible: ${(notificationContext as any).isPageVisible}
🏠 On Community Page: ${(notificationContext as any).isOnCommunityPage}
💬 Current Group: ${(notificationContext as any).currentGroupId || 'None'}
🔔 Permission: ${(notificationContext as any).permissionStatus}
✅ Supported: ${(notificationContext as any).isNotificationSupported}

Test by sending a message from another browser/device.`);
     } catch (error) {
       console.error('Error getting notification context:', error);
       setError('Failed to get notification context');
     }
   };

       const handleTestBrowserNotification = async () => {
    if (!currentUser || !isAdmin) return;

    try {
      // Check browser support
      if (!('Notification' in window)) {
        setError('Browser notifications not supported');
        return;
      }

      // Test direct browser notification
      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setError('Notification permission denied');
          return;
        }
      }

      // Detect mobile for simplified notification options
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      
      let testOptions: NotificationOptions;
      
      if (isMobile) {
        // Simplified for mobile
        testOptions = {
          body: 'Test notification - mobile browser',
          tag: 'browser-test'
        };
      } else if (isChrome) {
        // Chrome desktop
        testOptions = {
          body: 'Test notification - Chrome desktop',
          tag: 'browser-test-' + Date.now(),
          silent: false,
          requireInteraction: false
        };
      } else {
        // Safari and others
        testOptions = {
          body: 'Test notification - desktop browser',
          icon: '/images/Logos/T1 Logo Black200.png',
          tag: 'browser-test'
        };
      }

      // Mobile browsers require Service Worker
      const requiresServiceWorker = isMobile;
      
      if (requiresServiceWorker && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification('Test Notification', testOptions);
          setError(null);
        } catch (swError) {
          // Fallback to direct notification
          try {
            const testNotification = new Notification('Test Notification', testOptions);
            setTimeout(() => testNotification.close(), 5000);
            setError(null);
          } catch (directError) {
            throw new Error(`Both Service Worker and direct notifications failed`);
          }
        }
        
      } else {
        // Desktop browsers - use direct notification
        try {
          const testNotification = new Notification('Test Notification', testOptions);
          
          testNotification.onclick = () => {
            testNotification.close();
          };

          setTimeout(() => testNotification.close(), 5000);
          setError(null);

        } catch (notificationError) {
          // Try with minimal options for Chrome
          if (isChrome) {
            try {
              const fallbackNotification = new Notification('Test', { body: 'Simple test' });
              setTimeout(() => fallbackNotification.close(), 3000);
              setError(null);
            } catch (fallbackError) {
              throw new Error(`Chrome notification failed: ${fallbackError instanceof Error ? fallbackError.message : String(fallbackError)}`);
            }
          } else {
            throw notificationError;
          }
        }
      }

    } catch (error) {
      console.error('Browser notification test failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError('Browser notification test failed: ' + errorMessage);
    }
  };

       const handleCloseChat = () => {
      // Track chat close
      if (analytics && selectedGroup) {
        logEvent(analytics, 'group_chat_close', {
          group_id: selectedGroup.id,
          group_name: selectedGroup.name,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }

      setSelectedGroup(null);
      notificationService.setCurrentGroup(null);
    };

   // Handle notification card interactions
   const handleCloseNotification = (notificationId: string) => {
     notificationService.removeInBrowserNotification(notificationId);
   };

   const handleClickNotification = (notification: NotificationCardData) => {
     // Navigate to the group if specified
     if (notification.groupId) {
       const group = groups.find(g => g.id === notification.groupId);
       if (group) {
         setSelectedGroup(group);
         notificationService.setCurrentGroup(group.id);
       }
     }
     
     // Remove the notification
     notificationService.removeInBrowserNotification(notification.id);
   };

  const handleImageViewerOpen = (imageUrl: string) => {
    setSelectedImageForViewer(imageUrl);
    setImageViewerOpen(true);
  };

  const handleImageViewerClose = () => {
    setSelectedImageForViewer(null);
    setImageViewerOpen(false);
  };

  // Handle keyboard shortcuts for image viewer
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (imageViewerOpen) {
        if (event.key === 'Escape') {
          handleImageViewerClose();
        } else if (event.key === 'd' || event.key === 'D') {
          if (selectedImageForViewer) {
            handleImageDownload(selectedImageForViewer, 'community-image');
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [imageViewerOpen, selectedImageForViewer]);

  /**
   * Open image in new tab
   */
  const handleImageDownload = async (imageUrl: string, imageName?: string) => {
    console.log('Attempting to download image:', imageUrl);
    
    // Show loading feedback
    setCompressionStatus('📥 Downloading image...');
    
    // Simple approach: Try download first, if it fails, open in new tab
    try {
      // Check if it's a Firebase Storage URL
      const isFirebaseStorage = imageUrl.includes('firebasestorage.googleapis.com');
      console.log('Is Firebase Storage URL:', isFirebaseStorage);
      
      let downloadSuccessful = false;
      
      if (isFirebaseStorage) {
        // Try Firebase SDK method first
        try {
          console.log('Trying Firebase SDK download...');
          const urlObj = new URL(imageUrl);
          console.log('URL pathname:', urlObj.pathname);
          
          // Extract storage path from Firebase Storage URL
          // Format: /v0/b/{bucket}/o/{path}
          const pathMatch = urlObj.pathname.match(/\/v0\/b\/[^\/]+\/o\/(.+)$/);
          if (!pathMatch) {
            throw new Error('Could not extract storage path from URL');
          }
          
          const storagePath = decodeURIComponent(pathMatch[1]);
          console.log('Extracted storage path:', storagePath);
          const storageRef = ref(storage, storagePath);
          const blob = await getBlob(storageRef);
          
          // Generate filename
          let filename = 'image.jpg';
          if (imageName) {
            // Extract clean filename from original name (remove timestamps and prefixes)
            const cleanName = imageName.replace(/^\d+-/, ''); // Remove timestamp prefix like "1749885113977-"
            filename = cleanName;
          } else {
            // Get file extension from blob type
            const extension = blob.type.split('/')[1] || 'jpg';
            filename = `image.${extension}`;
          }
          
          // Create and trigger download
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
          
          downloadSuccessful = true;
          setCompressionStatus('✅ Image downloaded successfully!');
          setTimeout(() => setCompressionStatus(null), 2000);
          
        } catch (firebaseError) {
          console.warn('Firebase SDK failed:', firebaseError);
          // Will fall through to new tab fallback
        }
      } else {
        // Try fetch method for non-Firebase URLs
        try {
          console.log('Trying fetch download...');
          const response = await fetch(imageUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const blob = await response.blob();
          
          // Generate filename
          let filename = 'image.jpg';
          if (imageName) {
            // Extract clean filename from original name (remove timestamps and prefixes)
            const cleanName = imageName.replace(/^\d+-/, ''); // Remove timestamp prefix like "1749885113977-"
            filename = cleanName;
          } else {
            // Get file extension from blob type
            const extension = blob.type.split('/')[1] || 'jpg';
            filename = `image.${extension}`;
          }
          
          // Create and trigger download
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
          
          downloadSuccessful = true;
          setCompressionStatus('✅ Image downloaded successfully!');
          setTimeout(() => setCompressionStatus(null), 2000);
          
        } catch (fetchError) {
          console.warn('Fetch failed:', fetchError);
          // Will fall through to new tab fallback
        }
      }
      
      // If download failed, open in new tab
      if (!downloadSuccessful) {
        console.log('Download failed, opening in new tab...');
        const newWindow = window.open(imageUrl, '_blank');
        
        if (newWindow) {
          setCompressionStatus('✅ Image opened in new tab (download failed)');
        } else {
          // Popup blocked - use link fallback
          console.log('Popup blocked, using link fallback...');
          const link = document.createElement('a');
          link.href = imageUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setCompressionStatus('✅ Image opened (download failed)');
        }
        
        setTimeout(() => setCompressionStatus(null), 3000);
      }
      
    } catch (error) {
      console.error('All download methods failed:', error);
      
      // Final fallback: open in new tab
      console.log('Final fallback: opening in new tab...');
      try {
        const newWindow = window.open(imageUrl, '_blank');
        
        if (newWindow) {
          setCompressionStatus('✅ Image opened in new tab');
        } else {
          // Popup blocked - use link fallback
          const link = document.createElement('a');
          link.href = imageUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setCompressionStatus('✅ Image opened');
        }
        
        setTimeout(() => setCompressionStatus(null), 3000);
      } catch (finalError) {
        console.error('Even new tab failed:', finalError);
        setCompressionStatus('❌ Could not download or open image');
        setTimeout(() => setCompressionStatus(null), 3000);
      }
    }
  };

  // Message edit/delete handlers
  const handleMessageMenuOpen = (event: React.MouseEvent<HTMLElement>, message: ChatMessage) => {
    event.stopPropagation();
    setMessageMenuAnchor(event.currentTarget);
    setSelectedMessageForMenu(message);
  };

  const handleMessageMenuClose = () => {
    setMessageMenuAnchor(null);
    setSelectedMessageForMenu(null);
  };

  const handleEditMessage = () => {
    if (!selectedMessageForMenu) return;
    setEditingMessageId(selectedMessageForMenu.id);
    setInputValue(selectedMessageForMenu.content); // Use main input box
    setEditingContent(selectedMessageForMenu.content); // Keep for reference
    handleMessageMenuClose();
    
    // Focus on the input box
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
        messageInputRef.current.setSelectionRange(
          messageInputRef.current.value.length,
          messageInputRef.current.value.length
        );
      }
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingContent('');
    setInputValue(''); // Clear main input
  };

  const handleReplyMessage = () => {
    if (!selectedMessageForMenu) return;
    setReplyingTo(selectedMessageForMenu);
    handleMessageMenuClose();
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleReplyClick = async (replyToMessageId: string) => {
    // First, check if the message is already visible
    const messageElement = document.querySelector(`[data-message-id="${replyToMessageId}"]`);
    
    if (messageElement && messagesContainerRef.current) {
      // Message is visible, scroll to it
      messageElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Highlight the message temporarily
      setHighlightedMessageId(replyToMessageId);
      
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);
    } else {
      // Message not visible, check if it exists in current messages array
      const messageExists = messages.some(msg => msg.id === replyToMessageId);
      
      if (!messageExists && hasMoreMessages && selectedGroup) {
        // Message might be in older messages, try to load more
        setCompressionStatus('🔍 Looking for original message...');
        
        try {
          // Load older messages until we find the target message or run out
          let foundMessage = false;
          let attempts = 0;
          const maxAttempts = 5; // Prevent infinite loading
          
          while (!foundMessage && hasMoreMessages && attempts < maxAttempts) {
            await loadOlderMessages();
            
            // Wait a bit for the messages to load
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Check if the message is now visible
            const updatedMessageElement = document.querySelector(`[data-message-id="${replyToMessageId}"]`);
            if (updatedMessageElement) {
              foundMessage = true;
              
              // Scroll to the found message
              updatedMessageElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
              
              // Highlight the message
              setHighlightedMessageId(replyToMessageId);
              setTimeout(() => setHighlightedMessageId(null), 3000);
              
              setCompressionStatus('✅ Found original message!');
              setTimeout(() => setCompressionStatus(null), 2000);
            }
            
            attempts++;
          }
          
          if (!foundMessage) {
            setCompressionStatus('❌ Original message not found in recent history');
            setTimeout(() => setCompressionStatus(null), 3000);
          }
        } catch (error) {
          console.error('Error loading older messages:', error);
          setCompressionStatus('❌ Error loading older messages');
          setTimeout(() => setCompressionStatus(null), 3000);
        }
      } else {
        // Message doesn't exist or no more messages to load
        setCompressionStatus('❌ Original message not found');
        setTimeout(() => setCompressionStatus(null), 3000);
      }
    }
  };



  const handleDeleteMessage = () => {
    if (!selectedMessageForMenu) return;
    setMessageToDelete(selectedMessageForMenu);
    setDeleteConfirmOpen(true);
    handleMessageMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!messageToDelete || !selectedGroup) return;

    try {
      const messageRef = doc(db, 'groups', selectedGroup.id, 'messages', messageToDelete.id);
      await deleteDoc(messageRef);
      
      setDeleteConfirmOpen(false);
      setMessageToDelete(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete message');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setMessageToDelete(null);
  };

  const canEditMessage = (message: ChatMessage) => {
    return currentUser && message.userId === currentUser.uid && message.type === 'text';
  };

  const canDeleteMessage = (message: ChatMessage) => {
    return currentUser && (message.userId === currentUser.uid || isAdmin);
  };

  const canPostInGroup = (group: Group | null) => {
    if (!group) return false;
    if (!group.adminOnlyPosting) return true; // Anyone can post if not admin-only
    return isAdmin; // Only admins can post if admin-only is enabled
  };

  // Memoize expensive calculations to prevent recalculation on every render
  const isSendDisabled = useMemo(() => {
    return (!inputValue.trim() && !selectedImage) || sendingMessage || uploadingImage || !canPostInGroup(selectedGroup);
  }, [inputValue, selectedImage, sendingMessage, uploadingImage, selectedGroup]);
  
  const isImageDisabled = useMemo(() => {
    return sendingMessage || uploadingImage || !canPostInGroup(selectedGroup);
  }, [sendingMessage, uploadingImage, selectedGroup]);

  const canShowMessageMenu = (message: ChatMessage) => {
    // Show menu for all messages when user is authenticated (for reply functionality)
    return currentUser !== null;
  };

  // Reaction handlers
  const handleReactionAdd = async (emoji: string) => {
    if (!selectedMessageForReaction || !currentUser || !selectedGroup) return;

    try {
      const messageRef = doc(db, 'groups', selectedGroup.id, 'messages', selectedMessageForReaction.id);
      const currentReactions = selectedMessageForReaction.reactions || {};
      
      // Check if user already reacted with this emoji
      const existingReaction = currentReactions[emoji];
      const userAlreadyReacted = existingReaction?.users.includes(currentUser.uid);

      let updatedReactions;
      if (userAlreadyReacted) {
        // Remove user's reaction
        const updatedUsers = existingReaction.users.filter(uid => uid !== currentUser.uid);
        if (updatedUsers.length === 0) {
          // Remove emoji entirely if no users left
          updatedReactions = { ...currentReactions };
          delete updatedReactions[emoji];
        } else {
          updatedReactions = {
            ...currentReactions,
            [emoji]: {
              users: updatedUsers,
              count: updatedUsers.length
            }
          };
        }
      } else {
        // Add user's reaction
        updatedReactions = {
          ...currentReactions,
          [emoji]: {
            users: existingReaction ? [...existingReaction.users, currentUser.uid] : [currentUser.uid],
            count: existingReaction ? existingReaction.count + 1 : 1
          }
        };
      }

      await updateDoc(messageRef, {
        reactions: updatedReactions
      });

      setReactionPickerAnchor(null);
      setSelectedMessageForReaction(null);
    } catch (error) {
      console.error('Error updating reaction:', error);
      setError(error instanceof Error ? error.message : 'Failed to update reaction');
    }
  };

  const handleReactionPickerOpen = (event: React.MouseEvent<HTMLElement>, message: ChatMessage) => {
    event.stopPropagation();
    setReactionPickerAnchor(event.currentTarget);
    setSelectedMessageForReaction(message);
  };

  const handleReactionPickerClose = () => {
    setReactionPickerAnchor(null);
    setSelectedMessageForReaction(null);
  };

  const handleQuickReaction = async (emoji: string, message: ChatMessage) => {
    if (!currentUser || !selectedGroup) return;

    try {
      const messageRef = doc(db, 'groups', selectedGroup.id, 'messages', message.id);
      const currentReactions = message.reactions || {};
      
      // Check if user already reacted with this emoji
      const existingReaction = currentReactions[emoji];
      const userAlreadyReacted = existingReaction?.users.includes(currentUser.uid);

      let updatedReactions;
      if (userAlreadyReacted) {
        // Remove user's reaction
        const updatedUsers = existingReaction.users.filter(uid => uid !== currentUser.uid);
        if (updatedUsers.length === 0) {
          // Remove emoji entirely if no users left
          updatedReactions = { ...currentReactions };
          delete updatedReactions[emoji];
        } else {
          updatedReactions = {
            ...currentReactions,
            [emoji]: {
              users: updatedUsers,
              count: updatedUsers.length
            }
          };
        }
      } else {
        // Add user's reaction
        updatedReactions = {
          ...currentReactions,
          [emoji]: {
            users: existingReaction ? [...existingReaction.users, currentUser.uid] : [currentUser.uid],
            count: existingReaction ? existingReaction.count + 1 : 1
          }
        };
      }

      await updateDoc(messageRef, {
        reactions: updatedReactions
      });
    } catch (error) {
      console.error('Error updating reaction:', error);
      setError(error instanceof Error ? error.message : 'Failed to update reaction');
    }
  };

  // Add WhatsApp-like quick reaction handlers
  const handleQuickReactionOpen = (event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>, message: ChatMessage) => {
    event.stopPropagation();
    setQuickReactionAnchor(event.currentTarget as HTMLElement);
    setSelectedMessageForQuickReaction(message);
  };

  const handleQuickReactionClose = () => {
    setQuickReactionAnchor(null);
    setSelectedMessageForQuickReaction(null);
  };

  const handleQuickReactionSelect = async (emoji: string) => {
    if (!selectedMessageForQuickReaction) return;
    
    await handleQuickReaction(emoji, selectedMessageForQuickReaction);
    handleQuickReactionClose();
  };

  // Long press handlers for mobile
  const handleLongPressStart = (event: React.TouchEvent<HTMLElement>, message: ChatMessage) => {
    setIsLongPress(false);
    const timer = setTimeout(() => {
      setIsLongPress(true);
      handleQuickReactionOpen(event, message);
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500); // 500ms for long press
    
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    handleLongPressEnd();
    if (!isLongPress) {
      // Handle normal tap if it wasn't a long press
      // This prevents interfering with normal touch interactions
    }
    setIsLongPress(false);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 8 
          }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Loading Community
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {loadingGroups && loadingUserGroups 
                ? "Loading groups and checking your memberships..."
                : loadingGroups 
                  ? "Loading groups..." 
                  : "Checking your group memberships..."}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <>
      {/* In-browser notification cards */}
      <NotificationContainer
        notifications={inBrowserNotifications}
        onCloseNotification={handleCloseNotification}
        onClickNotification={handleClickNotification}
      />
      
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ mb: 2 }}>
            T1 FX Team Trading Community
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Connect with fellow traders, share insights, and grow together
          </Typography>
          
          {/* Community Online Users Indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <OnlineUsersIndicator type="community" variant="full" />
          </Box>
          
          {isAdmin && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  // Track admin panel open
                  if (analytics) {
                    logEvent(analytics, 'admin_panel_open', {
                      device_type: isMobile ? 'mobile' : 'desktop'
                    });
                  }
                  
                  setAdminPanelOpen(true);
                }}
              >
                Admin Panel
              </Button>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {compressionStatus && (
          <Alert 
            severity={compressionStatus.includes('🗜️') ? 'info' : 'success'} 
            sx={{ mb: 4 }} 
            onClose={() => setCompressionStatus(null)}
          >
            {compressionStatus}
          </Alert>
        )}

        {/* Mobile Layout: Show either groups or fullscreen chat */}
        {isMobile ? (
          <>
            {!selectedGroup ? (
              // Groups List on Mobile
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h4" gutterBottom>
                    Trading Groups
                  </Typography>
                  <Grid container spacing={2}>
                    {groups.map((group) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={group.id}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="h6" component="h3">
                                {group.name}
                              </Typography>
                              {group.adminOnlyPosting && (
                                <Chip
                                  label="Admin Only"
                                  size="small"
                                  color="warning"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {group.description}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={`${group.memberCount || 0} members`}
                                  size="small"
                                  variant="outlined"
                                />
                                <OnlineUsersIndicator 
                                  type="group" 
                                  groupId={group.id} 
                                  groupName={group.name}
                                  variant="chip" 
                                />
                                {userGroups.includes(group.id) && unreadCounts[group.id] > 0 && (
                                  <Chip
                                    label={`${unreadCounts[group.id]} new`}
                                    size="small"
                                    color="primary"
                                    sx={{
                                      backgroundColor: 'primary.main',
                                      color: 'primary.contrastText',
                                      fontWeight: 'bold',
                                      animation: 'pulse 2s infinite'
                                    }}
                                  />
                                )}
                              </Box>
                              {userGroups.includes(group.id) ? (
                                <Box>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setSelectedGroup(group);
                                      
                                      // Track group chat open (non-blocking)
                                      if (analytics) {
                                        setTimeout(() => {
                                          logEvent(analytics, 'group_chat_open', {
                                            group_id: group.id,
                                            group_name: group.name,
                                            member_count: group.memberCount,
                                            unread_count: unreadCounts[group.id] || 0,
                                            device_type: isMobile ? 'mobile' : 'desktop'
                                          });
                                        }, 0);
                                      }
                                    }}
                                    sx={{ mr: 1 }}
                                  >
                                    Open Chat
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<LeaveIcon />}
                                    onClick={() => handleLeaveGroupClick(group)}
                                  >
                                    Leave
                                  </Button>
                                </Box>
                              ) : (
                                <Button
                                  size="small"
                                  color="primary"
                                  startIcon={<JoinIcon />}
                                  onClick={() => handleJoinGroupClick(group)}
                                >
                                  Join
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            ) : (
                              // Fullscreen Chat on Mobile
              <Box sx={{
                position: 'fixed',
                top: 96, // Account for navbar
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                backgroundColor: 'background.default'
              }}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 0,
                  boxShadow: 'none'
                }}>
                  <CardContent sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h5">
                            {selectedGroup.name}
                          </Typography>
                          {selectedGroup.adminOnlyPosting && (
                            <Chip
                              label="Admin Only"
                              size="small"
                              color="warning"
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <OnlineUsersIndicator 
                            type="group" 
                            groupId={selectedGroup.id} 
                            groupName={selectedGroup.name}
                            variant="chip" 
                          />
                          {getTranslationStatusDisplay()}
                        </Box>
                      </Box>
                      <Button
                        color="error"
                        onClick={handleCloseChat}
                      >
                        Close Chat
                      </Button>
                    </Box>
                  </CardContent>

                  {/* Messages */}
                  <Box 
                    ref={messagesContainerRef}
                    sx={{ 
                      flex: 1, 
                      overflow: 'auto', 
                      p: 2,
                      scrollBehavior: 'smooth',
                      display: 'flex',
                      flexDirection: 'column-reverse'
                    }}
                  >
                    <List sx={{ py: 0, display: 'flex', flexDirection: 'column-reverse' }}>
                      {messages.slice().reverse().map((message) => (
                        <MessageBubble
                          key={message.id}
                          message={message}
                          currentUserId={currentUser?.uid}
                          isMobile={isMobile}
                          highlightedMessageId={highlightedMessageId}
                          messagesContainerRef={messagesContainerRef}
                          formatTimestamp={formatTimestamp}
                          renderMessageContent={renderMessageContent}
                          canShowMessageMenu={canShowMessageMenu}
                          onMessageMenuOpen={handleMessageMenuOpen}
                          onImageViewerOpen={handleImageViewerOpen}
                          onImageDownload={handleImageDownload}
                          onQuickReaction={handleQuickReaction}
                          onQuickReactionOpen={handleQuickReactionOpen}
                          onLongPressStart={handleLongPressStart}
                          onLongPressEnd={handleLongPressEnd}
                          onTouchEnd={handleTouchEnd}
                          onReplyClick={handleReplyClick}
                        />
                      ))}
                      
                      {/* Load More Messages Button - Mobile (appears at top due to column-reverse) */}
                      {hasMoreMessages && !initialLoad && (
                        <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={loadOlderMessages}
                            disabled={loadingOlderMessages}
                            startIcon={loadingOlderMessages ? <CircularProgress size={16} /> : null}
                            sx={{
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: 'text.secondary',
                              '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)'
                              }
                            }}
                          >
                            {loadingOlderMessages ? 'Loading...' : 'Load More Messages'}
                          </Button>
                        </ListItem>
                      )}
                    </List>
                  </Box>

                  {/* Message Input */}
                  <Paper
                    component="form"
                    onSubmit={handleSendMessage}
                    sx={{ p: 2, borderRadius: 0 }}
                  >
                    {/* Edit Preview - Mobile */}
                    {editingMessageId && (
                      <Box sx={{ 
                        mb: 2, 
                        p: 2, 
                        backgroundColor: 'rgba(255, 152, 0, 0.1)', 
                        borderLeft: '4px solid',
                        borderLeftColor: 'warning.main',
                        borderRadius: '4px'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                            ✏️ Editing your message from {(() => {
                              const message = messages.find(m => m.id === editingMessageId);
                              return message ? formatTimestamp(message.timestamp) : 'now';
                            })()}
                          </Typography>
                          <IconButton size="small" onClick={handleCancelEdit}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          Original: "{editingContent}"
                        </Typography>
                      </Box>
                    )}

                    {/* Reply Preview - Mobile */}
                    {replyingTo && (
                      <Box sx={{ 
                        mb: 2, 
                        p: 2, 
                        backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                        borderLeft: '4px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '4px'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                            Replying to {replyingTo.userDisplayName}
                          </Typography>
                          <IconButton size="small" onClick={handleCancelReply}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {replyingTo.type === 'image' ? '📷 Image' : replyingTo.content}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Admin-only posting warning */}
                    {selectedGroup?.adminOnlyPosting && !canPostInGroup(selectedGroup) && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          Only administrators can post messages in this group.
                        </Typography>
                      </Alert>
                    )}
                    
                    {/* Image Preview */}
                    {imagePreview && (
                      <Box sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Image Preview
                          </Typography>
                          <IconButton size="small" onClick={clearSelectedImage}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{
                            maxWidth: '100px',
                            maxHeight: '100px',
                            borderRadius: '4px',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    )}
                    
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={6}
                        variant="outlined"
                        placeholder={
                          editingMessageId 
                            ? "Edit your message..." 
                            : canPostInGroup(selectedGroup) 
                              ? "Type your message..." 
                              : "Only admins can post in this group"
                        }
                        value={inputValue}
                        onChange={handleMessageInputChange}
                        onKeyDown={handleKeyDown}
                        disabled={sendingMessage || uploadingImage || !canPostInGroup(selectedGroup)}
                        inputRef={messageInputRef}
                        InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              onClick={handleEmojiOpen}
                              color="primary"
                              size="small"
                              disabled={sendingMessage || uploadingImage || !canPostInGroup(selectedGroup)}
                            >
                              <EmojiIcon />
                            </IconButton>
                            <IconButton
                              onClick={handleImageButtonClick}
                              color="primary"
                              size="small"
                              disabled={sendingMessage || uploadingImage || !canPostInGroup(selectedGroup)}
                            >
                              <ImageIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              type="submit"
                              disabled={(!inputValue.trim() && !selectedImage && !selectedAudio) || sendingMessage || uploadingImage || uploadingAudio || !canPostInGroup(selectedGroup)}
                              color="primary"
                            >
                              {sendingMessage || uploadingImage || uploadingAudio ? <CircularProgress size={24} /> : <SendIcon />}
                            </IconButton>
                            <VoiceMicButton
                              recording={recording}
                              recordingDuration={recordingDuration}
                              audioPreview={audioPreview}
                              onMicClick={handleMicButtonClick}
                              onClearAudio={clearSelectedAudio}
                              disabled={sendingMessage || uploadingImage || uploadingAudio || !canPostInGroup(selectedGroup)}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    {/* @mention suggestions dropdown */}
                    {showMentions && mentionSuggestions.length > 0 && (
                      <Paper
                        sx={{
                          position: 'absolute',
                          bottom: '100%',
                          left: 0,
                          right: 0,
                          mb: 1,
                          maxHeight: 200,
                          overflow: 'auto',
                          zIndex: 1000,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      >
                        <List sx={{ py: 0 }}>
                          {mentionSuggestions.map((member) => (
                            <ListItem
                              key={member.userId}
                              disablePadding
                            >
                              <ListItemText
                                sx={{
                                  py: 1,
                                  px: 2,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                  }
                                }}
                                onClick={() => handleMentionSelect(member)}
                              >
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    mr: 1,
                                    fontSize: '12px',
                                    bgcolor: 'primary.main'
                                  }}
                                >
                                  {member.userDisplayName[0]?.toUpperCase() || 'A'}
                                </Avatar>
                                <Typography variant="body2">
                                  {member.userDisplayName}
                                </Typography>
                              </ListItemText>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>

                    <VoiceRecorder
                      recording={recording}
                      recordingDuration={recordingDuration}
                      audioPreview={audioPreview}
                      onMicClick={handleMicButtonClick}
                      onClearAudio={clearSelectedAudio}
                    />
                    
                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                  </Paper>
                </Card>
              </Box>
            )}
          </>
        ) : (
          // Desktop Layout: Side-by-side groups and chat
          <Grid container spacing={4}>
            {/* Groups List - Hide on mobile when chat is open */}
            {(!isMobile || !selectedGroup) && (
              <Grid size={{ xs: 12, md: selectedGroup ? 4 : 12 }}>
                <Typography variant="h4" gutterBottom>
                  Trading Groups
                </Typography>
                <Grid container spacing={2}>
                  {groups.map((group) => (
                    <Grid size={{ xs: 12, sm: selectedGroup ? 12 : 6, md: selectedGroup ? 12 : 4 }} key={group.id}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6" component="h3">
                              {group.name}
                            </Typography>
                            {group.adminOnlyPosting && (
                              <Chip
                                label="Admin Only"
                                size="small"
                                color="warning"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {group.description}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={`${group.memberCount || 0} members`}
                                size="small"
                                variant="outlined"
                              />
                              <OnlineUsersIndicator 
                                type="group" 
                                groupId={group.id} 
                                groupName={group.name}
                                variant="chip" 
                              />
                              {userGroups.includes(group.id) && unreadCounts[group.id] > 0 && (
                                <Chip
                                  label={`${unreadCounts[group.id]} new`}
                                  size="small"
                                  color="primary"
                                  sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    fontWeight: 'bold',
                                    animation: 'pulse 2s infinite'
                                  }}
                                />
                              )}
                            </Box>
                            {userGroups.includes(group.id) ? (
                              <Box>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setSelectedGroup(group);
                                    
                                    // Track group chat open (non-blocking)
                                    if (analytics) {
                                      setTimeout(() => {
                                        logEvent(analytics, 'group_chat_open', {
                                          group_id: group.id,
                                          group_name: group.name,
                                          member_count: group.memberCount,
                                          unread_count: unreadCounts[group.id] || 0,
                                          device_type: isMobile ? 'mobile' : 'desktop'
                                        });
                                      }, 0);
                                    }
                                  }}
                                  sx={{ mr: 1 }}
                                >
                                  Open Chat
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<LeaveIcon />}
                                  onClick={() => handleLeaveGroupClick(group)}
                                >
                                  Leave
                                </Button>
                              </Box>
                            ) : (
                              <Button
                                size="small"
                                color="primary"
                                startIcon={<JoinIcon />}
                                onClick={() => handleJoinGroupClick(group)}
                              >
                                Join
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {/* Chat Area - Fullscreen on mobile, side panel on desktop */}
            {selectedGroup && (
              <Grid size={{ xs: 12, md: 8 }} sx={isMobile ? { 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                right: 0, 
                width: '100vw',
                maxWidth: 'none',
                zIndex: 1299
              } : {}}>
                <Card sx={{ 
                  height: isMobile ? 'calc(100vh - 96px)' : '70vh', 
                  display: 'flex', 
                  flexDirection: 'column',
                  ...(isMobile ? {
                    position: 'fixed',
                    top: 96, // 12 units * 8px = 96px (xs: 12 from main container)
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1300,
                    borderRadius: 0,
                    height: 'calc(100vh - 96px)',
                    margin: 0,
                    width: '100vw',
                    maxWidth: 'none'
                  } : {})
                }}>
                  <CardContent sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h5">
                            {selectedGroup.name}
                          </Typography>
                          {selectedGroup.adminOnlyPosting && (
                            <Chip
                              label="Admin Only"
                              size="small"
                              color="warning"
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <OnlineUsersIndicator 
                            type="group" 
                            groupId={selectedGroup.id} 
                            groupName={selectedGroup.name}
                            variant="chip" 
                          />
                          {getTranslationStatusDisplay()}
                        </Box>
                      </Box>
                      <Button
                        color="error"
                        onClick={handleCloseChat}
                      >
                        Close Chat
                      </Button>
                    </Box>
                  </CardContent>

                  {/* Messages */}
                  <Box 
                    ref={messagesContainerRef}
                    sx={{ 
                      flex: 1, 
                      overflow: 'auto', 
                      p: 2,
                      scrollBehavior: 'smooth',
                      display: 'flex',
                      flexDirection: 'column-reverse'
                    }}
                  >
                    <List sx={{ py: 0, display: 'flex', flexDirection: 'column-reverse' }}>
                      {messages.slice().reverse().map((message) => (
                        <MessageBubble
                          key={message.id}
                          message={message}
                          currentUserId={currentUser?.uid}
                          isMobile={isMobile}
                          highlightedMessageId={highlightedMessageId}
                          messagesContainerRef={messagesContainerRef}
                          formatTimestamp={formatTimestamp}
                          renderMessageContent={renderMessageContent}
                          canShowMessageMenu={canShowMessageMenu}
                          onMessageMenuOpen={handleMessageMenuOpen}
                          onImageViewerOpen={handleImageViewerOpen}
                          onImageDownload={handleImageDownload}
                          onQuickReaction={handleQuickReaction}
                          onQuickReactionOpen={handleQuickReactionOpen}
                          onLongPressStart={handleLongPressStart}
                          onLongPressEnd={handleLongPressEnd}
                          onTouchEnd={handleTouchEnd}
                          onReplyClick={handleReplyClick}
                        />
                      ))}
                      
                      {/* Load More Messages Button - Desktop (appears at top due to column-reverse) */}
                      {hasMoreMessages && !initialLoad && (
                        <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={loadOlderMessages}
                            disabled={loadingOlderMessages}
                            startIcon={loadingOlderMessages ? <CircularProgress size={16} /> : null}
                            sx={{
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: 'text.secondary',
                              '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)'
                              }
                            }}
                          >
                            {loadingOlderMessages ? 'Loading...' : 'Load More Messages'}
                          </Button>
                        </ListItem>
                      )}
                    </List>
                  </Box>

                  {/* Message Input */}
                  <Paper
                    component="form"
                    onSubmit={handleSendMessage}
                    sx={{ p: 2, borderRadius: 0 }}
                  >
                    {/* Edit Preview - Desktop */}
                    {editingMessageId && (
                      <Box sx={{ 
                        mb: 2, 
                        p: 2, 
                        backgroundColor: 'rgba(255, 152, 0, 0.1)', 
                        borderLeft: '4px solid',
                        borderLeftColor: 'warning.main',
                        borderRadius: '4px'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                            ✏️ Editing your message from {(() => {
                              const message = messages.find(m => m.id === editingMessageId);
                              return message ? formatTimestamp(message.timestamp) : 'now';
                            })()}
                          </Typography>
                          <IconButton size="small" onClick={handleCancelEdit}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          Original: "{editingContent}"
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Reply Preview - Desktop */}
                    {replyingTo && (
                      <Box sx={{ 
                        mb: 2, 
                        p: 2, 
                        backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                        borderLeft: '4px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '4px'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                            Replying to {replyingTo.userDisplayName}
                          </Typography>
                          <IconButton size="small" onClick={handleCancelReply}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {replyingTo.type === 'image' ? '📷 Image' : replyingTo.content}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Admin-only posting warning */}
                    {selectedGroup?.adminOnlyPosting && !canPostInGroup(selectedGroup) && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          Only administrators can post messages in this group.
                        </Typography>
                      </Alert>
                    )}
                    
                    {/* Image Preview */}
                    {imagePreview && (
                      <Box sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Image Preview
                          </Typography>
                          <IconButton size="small" onClick={clearSelectedImage}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{
                            maxWidth: '100px',
                            maxHeight: '100px',
                            borderRadius: '4px',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    )}

                    <VoiceRecorder
                      recording={recording}
                      recordingDuration={recordingDuration}
                      audioPreview={audioPreview}
                      onMicClick={handleMicButtonClick}
                      onClearAudio={clearSelectedAudio}
                    />
                    
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={6}
                        variant="outlined"
                        placeholder={
                          editingMessageId 
                            ? "Edit your message..." 
                            : canPostInGroup(selectedGroup) 
                              ? "Type your message..." 
                              : "Only admins can post in this group"
                        }
                        value={inputValue}
                        onChange={handleMessageInputChange}
                        onKeyDown={handleKeyDown}
                        disabled={sendingMessage || uploadingImage || uploadingAudio || recording || !canPostInGroup(selectedGroup)}
                        inputRef={messageInputRef}
                        InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              onClick={handleEmojiOpen}
                              color="primary"
                              size="small"
                              disabled={sendingMessage || uploadingImage || uploadingAudio || recording || !canPostInGroup(selectedGroup)}
                            >
                              <EmojiIcon />
                            </IconButton>
                            <IconButton
                              onClick={handleImageButtonClick}
                              color="primary"
                              size="small"
                              disabled={sendingMessage || uploadingImage || uploadingAudio || recording || !canPostInGroup(selectedGroup)}
                            >
                              <ImageIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              type="submit"
                              disabled={(!inputValue.trim() && !selectedImage && !selectedAudio) || sendingMessage || uploadingImage || uploadingAudio || !canPostInGroup(selectedGroup)}
                              color="primary"
                            >
                              {sendingMessage || uploadingImage || uploadingAudio ? <CircularProgress size={24} /> : <SendIcon />}
                            </IconButton>
                            <VoiceMicButton
                              recording={recording}
                              recordingDuration={recordingDuration}
                              audioPreview={audioPreview}
                              onMicClick={handleMicButtonClick}
                              onClearAudio={clearSelectedAudio}
                              disabled={sendingMessage || uploadingImage || uploadingAudio || !canPostInGroup(selectedGroup)}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    {/* @mention suggestions dropdown */}
                    {showMentions && mentionSuggestions.length > 0 && (
                      <Paper
                        sx={{
                          position: 'absolute',
                          bottom: '100%',
                          left: 0,
                          right: 0,
                          mb: 1,
                          maxHeight: 200,
                          overflow: 'auto',
                          zIndex: 1000,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      >
                        <List sx={{ py: 0 }}>
                          {mentionSuggestions.map((member) => (
                            <ListItem
                              key={member.userId}
                              disablePadding
                            >
                              <ListItemText
                                sx={{
                                  py: 1,
                                  px: 2,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                  }
                                }}
                                onClick={() => handleMentionSelect(member)}
                              >
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    mr: 1,
                                    fontSize: '12px',
                                    bgcolor: 'primary.main'
                                  }}
                                >
                                  {member.userDisplayName[0]?.toUpperCase() || 'A'}
                                </Avatar>
                                <Typography variant="body2">
                                  {member.userDisplayName}
                                </Typography>
                              </ListItemText>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>
                    
                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                  </Paper>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {/* Emoji Picker Popover */}
        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={handleEmojiClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ 
            p: 2, 
            width: 280, 
            maxHeight: 300, 
            overflow: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 1
          }}>
            {emojis.map((emoji, index) => (
              <Button
                key={index}
                onClick={() => handleEmojiClick(emoji)}
                sx={{
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  p: 0,
                  fontSize: '18px',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {emoji}
              </Button>
            ))}
          </Box>
        </Popover>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationsEnabled}
                onChange={handleNotificationsToggle}
                disabled={notificationPermissionStatus === 'denied'}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {notificationPermissionStatus === 'granted' && notificationsEnabled ? (
                  <NotificationsIcon color="primary" />
                ) : (
                  <NotificationsOffIcon color="disabled" />
                )}
                <span>Receive notifications</span>
                {notificationPermissionStatus === 'denied' && (
                  <Tooltip title="Notifications are blocked. Please enable them in your browser settings.">
                    <Chip 
                      label="Blocked" 
                      size="small" 
                      color="error" 
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                {notificationPermissionStatus === 'default' && (
                  <Tooltip title="Click to enable notifications for this chat">
                    <Chip 
                      label="Allow" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                {notificationPermissionStatus === 'granted' && notificationsEnabled && (
                  <Tooltip title="Push notifications are enabled">
                    <Chip 
                      label="Active" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Box>
            }
          />
          
          {notificationPermissionStatus === 'denied' && (
            <Typography variant="caption" display="block" color="error" sx={{ mt: 1 }}>
              To enable notifications: Click the lock icon in your browser's address bar and allow notifications.
            </Typography>
          )}
          
          {notificationPermissionStatus === 'default' && (
            <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 1 }}>
              Enable notifications to get alerts when you receive new messages while away.
            </Typography>
          )}
        </Box>
      </Container>

      <AdminPanel
        open={adminPanelOpen}
        onClose={() => setAdminPanelOpen(false)}
      />

             {/* Image Viewer Modal */}
       {imageViewerOpen && selectedImageForViewer && (
         <Dialog
           open={imageViewerOpen}
           onClose={handleImageViewerClose}
           maxWidth={false}
           PaperProps={{
             sx: {
               backgroundColor: 'rgba(0, 0, 0, 0.95)',
               boxShadow: 'none',
               maxWidth: '95vw',
               maxHeight: '95vh',
               overflow: 'hidden',
               borderRadius: 2,
               m: 1
             }
           }}
           sx={{
             '& .MuiBackdrop-root': {
               backgroundColor: 'rgba(0, 0, 0, 0.8)'
             }
           }}
         >
           <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
             <IconButton
               onClick={handleImageViewerClose}
               sx={{
                 position: 'absolute',
                 top: 8,
                 right: 8,
                 zIndex: 1,
                 backgroundColor: 'rgba(255, 255, 255, 0.1)',
                 color: 'white',
                 '&:hover': {
                   backgroundColor: 'rgba(255, 255, 255, 0.2)'
                 }
               }}
             >
               <CloseIcon />
             </IconButton>
             <img
               src={selectedImageForViewer}
               alt="Full Size Image"
               style={{
                 maxWidth: '100%',
                 maxHeight: '85vh',
                 objectFit: 'contain',
                 borderRadius: '8px'
               }}
               onClick={(e) => e.stopPropagation()}
             />
           </DialogContent>
           <DialogActions sx={{ 
             backgroundColor: 'rgba(0, 0, 0, 0.9)', 
             borderTop: '1px solid rgba(255, 255, 255, 0.1)',
             justifyContent: 'space-between',
             px: 3,
             py: 2
           }}>
                           <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Press ESC to close • Press D to download image
              </Typography>
             <Box sx={{ display: 'flex', gap: 1 }}>
               <Button 
                 onClick={handleImageViewerClose}
                 sx={{ color: 'white' }}
               >
                 Close
               </Button>
               <Button
                 variant="contained"
                 startIcon={<DownloadIcon />}
                 onClick={() => {
                   handleImageDownload(selectedImageForViewer!, 'community-image');
                 }}
                 sx={{
                   backgroundColor: 'primary.main',
                   '&:hover': {
                     backgroundColor: 'primary.dark'
                   }
                 }}
               >
                 Download
               </Button>
             </Box>
           </DialogActions>
         </Dialog>
       )}

       {/* Message Menu */}
       <Menu
         anchorEl={messageMenuAnchor}
         open={Boolean(messageMenuAnchor)}
         onClose={handleMessageMenuClose}
         onClick={handleMessageMenuClose}
         PaperProps={{
           elevation: 3,
           sx: {
             overflow: 'visible',
             filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
             mt: 1.5,
             '& .MuiAvatar-root': {
               width: 32,
               height: 32,
               ml: -0.5,
               mr: 1,
             },
           },
         }}
         transformOrigin={{ horizontal: 'right', vertical: 'top' }}
         anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
               >
          {/* Reply option - available for all messages */}
          <MenuItem onClick={handleReplyMessage}>
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reply</ListItemText>
          </MenuItem>
          
          {/* Add Reaction option - available for all messages */}
          <MenuItem onClick={(e) => {
            handleMessageMenuClose();
            if (selectedMessageForMenu) {
              handleReactionPickerOpen(e, selectedMessageForMenu);
            }
          }}>
            <ListItemIcon>
              <AddReactionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Reaction</ListItemText>
          </MenuItem>
          
          {selectedMessageForMenu && canEditMessage(selectedMessageForMenu) && (
           <MenuItem onClick={handleEditMessage}>
             <ListItemIcon>
               <EditIcon fontSize="small" />
             </ListItemIcon>
             <ListItemText>Edit Message</ListItemText>
           </MenuItem>
         )}
         {selectedMessageForMenu && canDeleteMessage(selectedMessageForMenu) && (
           <MenuItem onClick={handleDeleteMessage} sx={{ color: 'error.main' }}>
             <ListItemIcon>
               <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
             </ListItemIcon>
             <ListItemText>Delete Message</ListItemText>
           </MenuItem>
         )}
       </Menu>

       {/* Reaction Picker */}
       <Popover
         open={Boolean(reactionPickerAnchor)}
         anchorEl={reactionPickerAnchor}
         onClose={handleReactionPickerClose}
         anchorOrigin={{
           vertical: 'bottom',
           horizontal: 'center',
         }}
         transformOrigin={{
           vertical: 'top',
           horizontal: 'center',
         }}
         PaperProps={{
           sx: {
             p: 1,
             borderRadius: 2,
             boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
           }
         }}
       >
         <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: '200px' }}>
           {['👍', '❤️', '😂', '😮', '😢', '😡', '🎉', '🔥', '👏', '💯'].map((emoji) => (
             <IconButton
               key={emoji}
               size="small"
               onClick={() => handleReactionAdd(emoji)}
               sx={{
                 fontSize: '16px',
                 minWidth: '32px',
                 height: '32px',
                 '&:hover': {
                   backgroundColor: 'rgba(0,0,0,0.04)',
                   transform: 'scale(1.1)'
                 }
               }}
             >
               {emoji}
             </IconButton>
           ))}
         </Box>
       </Popover>

       {/* Quick Reaction Picker (WhatsApp-style) */}
       <Popover
         open={Boolean(quickReactionAnchor)}
         anchorEl={quickReactionAnchor}
         onClose={handleQuickReactionClose}
         anchorOrigin={{
           vertical: 'top',
           horizontal: 'center',
         }}
         transformOrigin={{
           vertical: 'bottom',
           horizontal: 'center',
         }}
         PaperProps={{
           sx: {
             p: 0.5,
             borderRadius: 3,
             boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
             backgroundColor: 'background.paper',
             border: '1px solid rgba(255,255,255,0.1)',
           }
         }}
         transitionDuration={200}
       >
         <Box sx={{ 
           display: 'flex', 
           gap: 0.5, 
           alignItems: 'center',
           py: 0.5,
           px: 1
         }}>
           {['👍', '❤️', '😂', '😮', '😢', '😡'].map((emoji) => (
             <IconButton
               key={emoji}
               size="small"
               onClick={() => handleQuickReactionSelect(emoji)}
               sx={{
                 fontSize: '18px',
                 minWidth: '36px',
                 height: '36px',
                 borderRadius: '50%',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   backgroundColor: 'rgba(255,255,255,0.1)',
                   transform: 'scale(1.2)',
                   boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                 }
               }}
             >
               {emoji}
             </IconButton>
           ))}
           <Box sx={{ 
             width: '1px', 
             height: '24px', 
             backgroundColor: 'rgba(255,255,255,0.2)',
             mx: 0.5 
           }} />
           <IconButton
             size="small"
             onClick={() => {
               handleQuickReactionClose();
               if (selectedMessageForQuickReaction) {
                 handleReactionPickerOpen(quickReactionAnchor as any, selectedMessageForQuickReaction);
               }
             }}
             sx={{
               fontSize: '16px',
               minWidth: '36px',
               height: '36px',
               borderRadius: '50%',
               color: 'text.secondary',
               '&:hover': {
                 backgroundColor: 'rgba(255,255,255,0.1)',
                 transform: 'scale(1.1)'
               }
             }}
           >
             <AddReactionIcon fontSize="small" />
           </IconButton>
         </Box>
       </Popover>

       {/* Delete Confirmation Dialog */}
       <Dialog
         open={deleteConfirmOpen}
         onClose={handleCancelDelete}
         aria-labelledby="delete-dialog-title"
         aria-describedby="delete-dialog-description"
       >
         <DialogTitle id="delete-dialog-title">
           Delete Message
         </DialogTitle>
         <DialogContent>
           <Typography id="delete-dialog-description">
             Are you sure you want to delete this message? This action cannot be undone.
           </Typography>
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCancelDelete} color="primary">
             Cancel
           </Button>
           <Button onClick={handleConfirmDelete} color="error" variant="contained">
             Delete
           </Button>
         </DialogActions>
       </Dialog>

       {/* Join Group Confirmation Dialog */}
       <Dialog
         open={joinConfirmOpen}
         onClose={handleCancelJoinGroup}
         aria-labelledby="join-dialog-title"
         aria-describedby="join-dialog-description"
       >
         <DialogTitle id="join-dialog-title">
           Join Group
         </DialogTitle>
         <DialogContent>
           <Typography id="join-dialog-description">
             Are you sure you want to join "{groupToJoin?.name}"? You will be able to participate in group discussions and receive notifications for new messages.
           </Typography>
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCancelJoinGroup} color="primary">
             Cancel
           </Button>
           <Button onClick={handleConfirmJoinGroup} color="primary" variant="contained" startIcon={<JoinIcon />}>
             Join Group
           </Button>
         </DialogActions>
       </Dialog>

       {/* Leave Group Confirmation Dialog */}
       <Dialog
         open={leaveConfirmOpen}
         onClose={handleCancelLeaveGroup}
         aria-labelledby="leave-dialog-title"
         aria-describedby="leave-dialog-description"
       >
         <DialogTitle id="leave-dialog-title">
           Leave Group
         </DialogTitle>
         <DialogContent>
           <Typography id="leave-dialog-description">
             Are you sure you want to leave "{groupToLeave?.name}"? You will no longer be able to see group messages or participate in discussions. You can rejoin the group later if you wish.
           </Typography>
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCancelLeaveGroup} color="primary">
             Cancel
           </Button>
           <Button onClick={handleConfirmLeaveGroup} color="error" variant="contained" startIcon={<LeaveIcon />}>
             Leave Group
           </Button>
         </DialogActions>
       </Dialog>
    </Box>
    </>
  );
}; 

// --- Feature Guard Wrapper & Exports ---

function CommunityFeature() {
  return (
    <FeatureGuard featureId="community">
      <CommunityContent />
    </FeatureGuard>
  );
}

export const CommunityPage = CommunityFeature;
export const Community = CommunityFeature;

export default CommunityFeature;